import type { UFPMMember, UFPMFilter, UFPMImportResult } from './types'

const REQUIRED_HEADERS = [
  'Organization Name', 'Rank', 'Last Name', 'First Name',
  'Fitness Currency', 'Composite Score', 'Latest Fitness Assessment Category',
  'Latest Fitness Assessment Completion', 'Next Fitness Assessment Due Date',
]

export function getNowId(prefix: string): string {
  return prefix + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8)
}

export function cleanText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim()
}

export function normalizeAge(value: unknown): number {
  const age = parseInt(String(value ?? ''), 10)
  return Number.isFinite(age) && age > 0 ? age : 0
}

export function computeAge(dob: string | null): number {
  if (!dob) return 0
  const birth = new Date(dob + 'T00:00:00')
  if (isNaN(birth.getTime())) return 0
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  if (beforeBirthday) age--
  return age > 0 ? age : 0
}

export function validYmd(year: number, month: number, day: number): string | null {
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) return null
  return date.toISOString().slice(0, 10)
}

export function normalizeDate(value: unknown): string | null {
  const raw = cleanText(value)
  if (!raw) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw

  const excelSerial = Number(raw)
  if (Number.isFinite(excelSerial) && excelSerial > 20000 && excelSerial < 80000) {
    const date = new Date(Math.floor(excelSerial - 25569) * 86400000)
    return isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10)
  }

  const slash = raw.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})$/)
  if (slash && slash[1] && slash[2] && slash[3]) {
    const month = parseInt(slash[1], 10)
    const day   = parseInt(slash[2], 10)
    let year    = parseInt(slash[3], 10)
    if (year < 100) year += year > 70 ? 1900 : 2000
    return validYmd(year, month, day)
  }

  const parsed = new Date(raw)
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10)
  return null
}

export function normalizeStoredMember(member: unknown): UFPMMember | null {
  if (!member || typeof member !== 'object') return null
  const m = member as Record<string, unknown>
  const firstName = cleanText(m.firstName)
  const lastName  = cleanText(m.lastName)
  const fullName  = cleanText(m.fullName) || [firstName, lastName].filter(Boolean).join(' ')
  const dob = normalizeDate(m.dob)
  const age = computeAge(dob) || normalizeAge(m.age) || 0
  const now = Date.now()
  return {
    id:           String(m.id || getNowId('member')),
    organization: cleanText(m.organization),
    rank:         cleanText(m.rank),
    lastName,
    firstName,
    fullName,
    sex: m.sex === 'female' || m.sex === 'male' ? m.sex : '',
    dob,
    age,
    fitnessCurrency:                    cleanText(m.fitnessCurrency),
    compositeScore:                     cleanText(m.compositeScore),
    latestFitnessAssessmentCategory:    cleanText(m.latestFitnessAssessmentCategory),
    latestFitnessAssessmentCompletion:  normalizeDate(m.latestFitnessAssessmentCompletion),
    history:  Array.isArray(m.history) ? (m.history as UFPMMember['history']) : [],
    plans:    Array.isArray(m.plans)   ? (m.plans   as UFPMMember['plans'])   : [],
    lastTestDate:                (normalizeDate(m.lastTestDate) || normalizeDate(m.latestFitnessAssessmentCompletion)),
    nextFitnessAssessmentDueDate: normalizeDate(m.nextFitnessAssessmentDueDate),
    importedAt: Number(m.importedAt) || now,
    updatedAt:  Number(m.updatedAt)  || now,
    email: typeof m.email === 'string' ? m.email : undefined,
  }
}

export function memberNameKey(member: UFPMMember): string {
  return [member.lastName || '', member.firstName || ''].map(v => cleanText(v).toLowerCase()).join('|')
}

export function memberRosterKey(member: UFPMMember): string {
  return [member.lastName || '', member.firstName || '', member.organization || '', member.rank || '']
    .map(v => cleanText(v).toLowerCase()).join('|')
}

export function sortMembers(a: UFPMMember, b: UFPMMember): number {
  return (
    (a.lastName  || '').localeCompare(b.lastName  || '') ||
    (a.firstName || '').localeCompare(b.firstName || '') ||
    (a.rank      || '').localeCompare(b.rank      || '')
  )
}

export function getDueMonthKey(dateValue: unknown): string {
  const date = normalizeDate(dateValue)
  return date ? date.slice(0, 7) : ''
}

export function dueMonthLabel(monthKey: string): string {
  const match = String(monthKey || '').match(/^(\d{4})-(\d{2})$/)
  if (!match) return monthKey || ''
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1)
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

function scoreMember(member: UFPMMember, query: string): number {
  const last  = cleanText(member.lastName).toLowerCase()
  const first = cleanText(member.firstName).toLowerCase()
  const rank  = cleanText(member.rank).toLowerCase()
  const org   = cleanText(member.organization).toLowerCase()
  if (last === query)           return 100
  if (last.startsWith(query))  return 80
  if (last.includes(query))    return 60
  if (first === query)         return 50
  if (first.startsWith(query)) return 40
  if (rank.startsWith(query))  return 35
  if (first.includes(query))   return 30
  if (org.startsWith(query))   return 30
  if (rank.includes(query))    return 15
  if (org.includes(query))     return 10
  return 0
}

export function getFilteredMembers(
  members: UFPMMember[],
  search: string,
  filters: UFPMFilter,
): UFPMMember[] {
  const query = search.toLowerCase().trim()
  const filtered = members.filter(member => {
    const matchesSearch = !query || [
      member.fullName, member.rank, member.organization,
      member.fitnessCurrency, member.latestFitnessAssessmentCategory,
      member.nextFitnessAssessmentDueDate,
    ].some(v => cleanText(v).toLowerCase().includes(query))
    const matchesCurrency  = !filters.fitnessCurrency    || cleanText(member.fitnessCurrency) === filters.fitnessCurrency
    const matchesCategory  = !filters.assessmentCategory || cleanText(member.latestFitnessAssessmentCategory) === filters.assessmentCategory
    const matchesDueMonth  = !filters.dueMonth           || getDueMonthKey(member.nextFitnessAssessmentDueDate) === filters.dueMonth
    return matchesSearch && matchesCurrency && matchesCategory && matchesDueMonth
  })
  if (!query) return filtered
  return filtered.slice().sort((a, b) => scoreMember(b, query) - scoreMember(a, query))
}

export function mergeImportedMembers(
  existing: UFPMMember[],
  imported: UFPMMember[],
): { members: UFPMMember[]; added: number; updated: number } {
  const result = [...existing]
  let added = 0
  let updated = 0

  const incomingNameCounts = imported.reduce((map, m) => {
    const key = memberNameKey(m)
    map.set(key, (map.get(key) || 0) + 1)
    return map
  }, new Map<string, number>())
  const matchedIds = new Set<string>()

  imported.forEach(member => {
    const incomingKey = memberNameKey(member)
    const sameNameExisting = result.filter(m => !matchedIds.has(m.id) && memberNameKey(m) === incomingKey)
    let found: UFPMMember | null = null

    if (incomingNameCounts.get(incomingKey) === 1 && sameNameExisting.length <= 1) {
      found = sameNameExisting[0] || null
    } else {
      const rosterKey = memberRosterKey(member)
      found = sameNameExisting.find(m => memberRosterKey(m) === rosterKey) || null
    }

    if (found) {
      const idx = result.findIndex(m => m.id === found!.id)
      result[idx] = {
        ...member,
        id:         found.id,
        sex:        found.sex  || member.sex,
        dob:        found.dob  || member.dob,
        age:        computeAge(found.dob) || found.age || member.age,
        history:    found.history || [],
        plans:      found.plans   || [],
        importedAt: found.importedAt || member.importedAt,
        updatedAt:  Date.now(),
      }
      matchedIds.add(found.id)
      updated++
    } else {
      result.push(member)
      added++
    }
  })
  result.sort(sortMembers)
  return { members: result, added, updated }
}

// ---- CSV / delimited parsing ----

function cleanHeader(value: unknown): string {
  return cleanText(value).replace(/^﻿/, '')
}

function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/).find(Boolean) || ''
  const counts = [',', '\t', ';'].map(d => ({ d, n: firstLine.split(d).length }))
  counts.sort((a, b) => b.n - a.n)
  const best = counts[0]
  return best && best.n > 1 ? best.d : ','
}

function parseDelimitedRows(text: string): string[][] {
  const cleaned = String(text || '').replace(/^﻿/, '')
  const delimiter = detectDelimiter(cleaned)
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  for (let i = 0; i < cleaned.length; i++) {
    const ch   = cleaned[i]
    const next = cleaned[i + 1]
    if (inQuotes) {
      if (ch === '"' && next === '"') { cell += '"'; i++ }
      else if (ch === '"') inQuotes = false
      else cell += ch
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delimiter) {
      row.push(cell); cell = ''
    } else if (ch === '\n') {
      row.push(cell); rows.push(row); row = []; cell = ''
    } else if (ch !== '\r') {
      cell += ch
    }
  }
  row.push(cell)
  if (row.some(c => cleanText(c))) rows.push(row)
  return rows
}

function getHeaderValue(row: Record<string, unknown>, header: string, aliases: string[] = []): unknown {
  const wanted = [header, ...aliases].map(h => cleanHeader(h).toLowerCase())
  for (const key of Object.keys(row)) {
    if (wanted.includes(cleanHeader(key).toLowerCase())) return row[key]
  }
  return ''
}

function findMissingHeaders(row: Record<string, unknown>): string[] {
  const actual = new Set(Object.keys(row).map(h => cleanHeader(h).toLowerCase()))
  return REQUIRED_HEADERS.filter(h => !actual.has(h.toLowerCase()))
}

function normalizeRosterRow(row: Record<string, unknown>, importedAt: number): UFPMMember | null {
  const firstName = cleanText(getHeaderValue(row, 'First Name'))
  const lastName  = cleanText(getHeaderValue(row, 'Last Name'))
  const latestCompletion = normalizeDate(getHeaderValue(row, 'Latest Fitness Assessment Completion'))
  return normalizeStoredMember({
    id:           getNowId('member'),
    organization: getHeaderValue(row, 'Organization Name'),
    rank:         getHeaderValue(row, 'Rank'),
    lastName,
    firstName,
    fullName:     [firstName, lastName].filter(Boolean).join(' '),
    sex:          '',
    dob:          normalizeDate(getHeaderValue(row, 'Date of Birth', ['DOB', 'Birth Date'])),
    age:          normalizeAge(getHeaderValue(row, 'Age')),
    fitnessCurrency:                   getHeaderValue(row, 'Fitness Currency'),
    compositeScore:                    getHeaderValue(row, 'Composite Score'),
    latestFitnessAssessmentCategory:   getHeaderValue(row, 'Latest Fitness Assessment Category'),
    latestFitnessAssessmentCompletion: latestCompletion,
    history:     [],
    plans:       [],
    lastTestDate: latestCompletion,
    nextFitnessAssessmentDueDate: normalizeDate(getHeaderValue(row, 'Next Fitness Assessment Due Date')),
    importedAt,
    updatedAt: importedAt,
  })
}

function parseObjectRows(rows: Record<string, unknown>[]): UFPMImportResult {
  const importedAt = Date.now()
  const members: UFPMMember[] = []
  rows.forEach(row => {
    const member = normalizeRosterRow(row, importedAt)
    if (!member || (!member.firstName && !member.lastName)) return
    members.push(member)
  })
  if (!members.length) throw new Error('No roster members were found. Check that the official headers are present.')
  return { members, missingHeaders: findMissingHeaders(rows[0] || {}), rowCount: rows.length }
}

export function parseDelimitedRoster(text: string): UFPMImportResult {
  const rows = parseDelimitedRows(text)
  if (rows.length < 2) throw new Error('Roster file has no data rows.')
  const headers = (rows[0] || []).map(h => cleanHeader(h))
  const dataRows = rows.slice(1)
    .filter(row => row.some(cell => cleanText(cell)))
    .map(row => {
      const obj: Record<string, unknown> = {}
      headers.forEach((h, i) => { if (h) obj[h] = row[i] ?? '' })
      return obj
    })
  return parseObjectRows(dataRows)
}

type XLSXGlobal = {
  read: (buf: ArrayBuffer, opts: object) => { SheetNames: string[]; Sheets: Record<string, unknown> }
  utils: { sheet_to_json: (sheet: unknown, opts: object) => Record<string, unknown>[] }
}

export async function parseExcelRoster(file: File): Promise<UFPMImportResult> {
  const xlsx = (window as unknown as { XLSX?: XLSXGlobal }).XLSX
  if (!xlsx?.read) {
    throw new Error('Excel support did not load. Save the roster as CSV and upload again.')
  }
  const buffer = await file.arrayBuffer()
  const workbook = xlsx.read(buffer, { type: 'array', cellDates: true })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) throw new Error('The workbook does not contain a worksheet.')
  const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '', raw: false })
  return parseObjectRows(rows)
}

export async function parseRosterFile(file: File): Promise<UFPMImportResult> {
  if (!file) throw new Error('Choose a roster file first.')
  const name = file.name.toLowerCase()
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return parseExcelRoster(file)
  return parseDelimitedRoster(await file.text())
}
